/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Orders & Invoices History Table Component (Task 10.6.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  FileText,
  Search,
  Download,
  Receipt,
  CheckCircle2,
  Clock,
  RotateCcw,
  Copy,
  Check,
  CreditCard,
  Building2,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useSubscriptionStore } from '../../stores/useSubscriptionStore';

export const OrdersHistoryTable: React.FC = () => {
  const { t } = useTranslation();
  const {
    orderStatusFilter,
    searchQuery,
    setOrderStatusFilter,
    setSearchQuery,
    getFilteredOrders,
    openReceiptModal,
    downloadInvoicePdf,
  } = useSubscriptionStore();

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);

  const filteredOrders = getFilteredOrders();

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback
    }
  };

  const handleDownload = (orderId: string) => {
    downloadInvoicePdf(orderId);
    setDownloadSuccess(t('orders.downloadPdfSuccess'));
    setTimeout(() => setDownloadSuccess(null), 3000);
  };

  return (
    <div className="space-y-4">
      {/* Toast Notification */}
      {downloadSuccess && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-xs font-medium animate-in fade-in slide-in-from-top-2 duration-200">
          <CheckCircle2 className="size-4 flex-shrink-0 text-emerald-500" />
          <span>{downloadSuccess}</span>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3 rounded-2xl bg-surface border border-surface-border">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('orders.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-muted/60 text-xs text-foreground placeholder:text-foreground-muted border border-transparent focus:border-primary focus:bg-surface focus:outline-hidden transition-all"
          />
        </div>

        {/* Status Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          {(['all', 'paid', 'pending', 'refunded'] as const).map((status) => {
            const isActive = orderStatusFilter === status;
            return (
              <button
                key={status}
                type="button"
                onClick={() => setOrderStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-fg font-semibold shadow-xs'
                    : 'bg-muted/40 text-foreground-muted hover:text-foreground hover:bg-muted/80'
                }`}
              >
                {status === 'all'
                  ? t('orders.statusAll')
                  : status === 'paid'
                  ? t('orders.statusPaid')
                  : status === 'pending'
                  ? t('orders.statusPending')
                  : t('orders.statusRefunded')}
              </button>
            );
          })}
        </div>
      </div>

      {/* Orders Data Table */}
      <div className="rounded-2xl bg-surface border border-surface-border overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/40 text-foreground-muted border-b border-surface-border uppercase tracking-wider font-semibold text-[11px]">
              <tr>
                <th className="py-3 px-4">{t('orders.colInvoice')}</th>
                <th className="py-3 px-4">{t('orders.colDate')}</th>
                <th className="py-3 px-4">{t('orders.colPlan')}</th>
                <th className="py-3 px-4">{t('orders.colAmount')}</th>
                <th className="py-3 px-4">{t('orders.colGateway')}</th>
                <th className="py-3 px-4">{t('orders.colStatus')}</th>
                <th className="py-3 px-4 text-right">{t('orders.colActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <div className="max-w-sm mx-auto space-y-2">
                      <div className="size-10 rounded-full bg-muted flex items-center justify-center mx-auto text-foreground-muted">
                        <FileText className="size-5" />
                      </div>
                      <p className="font-semibold text-foreground text-sm">
                        {t('orders.emptyTitle')}
                      </p>
                      <p className="text-xs text-foreground-muted">
                        {t('orders.emptyDesc')}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => {
                  const dateStr = new Date(order.createdAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  });

                  return (
                    <tr
                      key={order.id}
                      className="hover:bg-muted/30 transition-colors group"
                    >
                      {/* Invoice # */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-1.5 font-mono text-foreground font-medium">
                          <span>{order.invoiceNumber}</span>
                          <button
                            type="button"
                            onClick={() => handleCopy(order.invoiceNumber, order.id)}
                            className="p-0.5 rounded text-foreground-muted hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t('common.copy')}
                          >
                            {copiedId === order.id ? (
                              <Check className="size-3 text-emerald-500" />
                            ) : (
                              <Copy className="size-3" />
                            )}
                          </button>
                        </div>
                      </td>

                      {/* Date */}
                      <td className="py-3.5 px-4 text-foreground-muted">
                        {dateStr}
                      </td>

                      {/* Description / Plan */}
                      <td className="py-3.5 px-4 font-medium text-foreground">
                        {order.description}
                      </td>

                      {/* Amount */}
                      <td className="py-3.5 px-4 font-mono font-semibold text-foreground">
                        ${order.amountPaid.toFixed(2)} {order.currency}
                      </td>

                      {/* Payment Gateway */}
                      <td className="py-3.5 px-4">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-surface border border-surface-border text-foreground">
                          {order.gatewayCode === 'stripe' ? (
                            <>
                              <CreditCard className="size-3 text-primary" />
                              <span>Stripe</span>
                            </>
                          ) : order.gatewayCode === 'bank_transfer' ? (
                            <>
                              <Building2 className="size-3 text-emerald-500" />
                              <span>VietQR</span>
                            </>
                          ) : (
                            <span>{order.gatewayCode}</span>
                          )}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4">
                        {order.status === 'paid' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25">
                            <CheckCircle2 className="size-3" />
                            <span>{t('orders.statusPaid')}</span>
                          </span>
                        ) : order.status === 'pending' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25">
                            <Clock className="size-3" />
                            <span>{t('orders.statusPending')}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/25">
                            <RotateCcw className="size-3" />
                            <span>{t('orders.statusRefunded')}</span>
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => openReceiptModal(order)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-surface hover:bg-surface-hover text-foreground border border-surface-border text-[11px] font-medium shadow-2xs transition-colors"
                            title={t('orders.viewReceipt')}
                          >
                            <Receipt className="size-3 text-foreground-muted" />
                            <span className="hidden sm:inline">{t('orders.viewReceipt')}</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDownload(order.id)}
                            className="p-1.5 rounded-lg bg-surface hover:bg-surface-hover text-foreground border border-surface-border shadow-2xs transition-colors"
                            title={t('orders.downloadPdf')}
                          >
                            <Download className="size-3.5 text-foreground-muted" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
